import {
  entryHeaderSet,
  readEntryIdxTag,
  workoutSetFromLogSet,
  type LogSet,
  type WorkoutExerciseEntry,
  type WorkoutSet,
} from '@heraion/shared';

// LG-71 — the index door's rows, regrouped into the one session the prefill
// actually asks about.
//
// ⭐ WHY THIS EXISTS. `getCachedHistoryForSlug` answers "what did you do last
// time" out of the rolling 50-SESSION window, and every prefill reads it. That
// window is not a time window: 36 of Antenore's 50 sessions on 2026-09-06 were
// Health Connect walks, one session each and no strength set in them, so the
// window reached back 20 days and five of the seven exercises he added that
// morning fell outside it. They were born with ONE empty set and no weight,
// while the same numbers sat one indexed query away — the query LG-49 already
// built and wired only to the history panel.
//
// This turns that query's answer into the SAME shape the window hands back, so
// `draftSetsFromHistoryEntry` stays the single translator into draft sets. ⛔
// There must not be a second prefill path: two of them would disagree the day
// one learns something the other does not.
//
// ⭐ Grouped by session AND by `entry:<n>`, not by session alone. An exercise
// performed twice in one workout (a lift opened, then revisited after a
// superset) is two entries in the stored shape, and merging them would offer
// the athlete six sets where they did three and three.
//
// ⛔ It rebuilds only what the prefill READS. No group, no equipment instance,
// no location: those are properties of the session that happened, not of the
// one being started. `useAddExercise` resolves the machine itself
// (`getSilentInstanceForSlug`, which knows what the athlete declared absent
// here), and carrying a `group` across would invent a superset nobody asked
// for.

/** `sessionId#entryIdx`, or the set's own day when the row is drop-in tracking
 *  with no session behind it. Two loose sets logged on the same day are one
 *  "last time"; two logged a month apart are not. */
const groupKeyOf = (set: LogSet): string => {
  const entryIdx = readEntryIdxTag(set);
  const position = entryIdx === null ? '_legacy' : String(entryIdx);
  if (set.sessionId) return `${set.sessionId}#${position}`;
  return `dropin:${set.performedAt.slice(0, 10)}#${position}`;
};

const atMsOf = (set: LogSet): number => Date.parse(set.performedAt);

/**
 * The most recent session's entry for these sets, or null when there is
 * nothing datable in them.
 *
 * @param sets Rows off `GET /api/sets?exercise=<slug>`. Order is not trusted —
 *   the route answers newest-first, but "which session is newest" is decided
 *   here by the timestamps rather than by position, so a caller that sorts
 *   differently cannot silently change which session gets prefilled.
 */
export function lastSessionEntryFromLogSets(
  sets: readonly LogSet[],
): WorkoutExerciseEntry | null {
  const groups = new Map<string, LogSet[]>();
  for (const set of sets) {
    // A row we cannot date cannot be placed in time, and a prefill built on
    // one would claim an age the age pill beside it would contradict.
    if (!Number.isFinite(atMsOf(set))) continue;
    const key = groupKeyOf(set);
    const bucket = groups.get(key);
    if (bucket) bucket.push(set);
    else groups.set(key, [set]);
  }

  let newest: LogSet[] | null = null;
  let newestAtMs = -Infinity;
  for (const members of groups.values()) {
    const at = Math.max(...members.map(atMsOf));
    if (at <= newestAtMs) continue;
    newestAtMs = at;
    newest = members;
  }
  if (newest === null || newest.length === 0) return null;

  // Ascending, like assembleWorkout: the sets must arrive in the order they
  // were performed, or the prefill would open with the last set first.
  const ordered = [...newest].sort((a, b) => atMsOf(a) - atMsOf(b));
  const first = ordered[0]!;
  const header = entryHeaderSet(ordered)!;
  const setObjs: WorkoutSet[] = ordered.map((m) =>
    workoutSetFromLogSet(m, { includeNote: m !== header }),
  );

  return {
    exerciseId: first.exerciseSlug,
    ...(first.exerciseName ? { exerciseName: first.exerciseName } : {}),
    sets: setObjs,
    ...(header.note ? { notes: header.note } : {}),
  };
}
